import React, { useEffect, useState } from 'react';
import {
  Building2,
  Plus,
  Trash2,
  Edit3,
  Search,
  AlertCircle,
  Users,
  UserCheck,
  Percent,
  DollarSign,
  FileText,
  Mail,
  Phone
} from 'lucide-react';
import { Client, ClientContact } from '../../../../types/Client.types';
import { PAYMENT_METHOD_LABELS, PAYMENT_TERMS_LABELS } from '../../../../constants/clients';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import ObexRequestsHandlerP80 from '../../../../handlers/ObexRequestsHandlerP80';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import Cookies from 'universal-cookie';
import '../shared-modules.css';
import './ClientsView.css';

const cookies = new Cookies();

const mapBackendToClient = (raw: any, contacts: ClientContact[] = []): Client => {
  let paymentTerms = '30_days';
  if (raw.payment_terms_days !== undefined && raw.payment_terms_days !== null) {
    const days = Number(raw.payment_terms_days);
    if (days === 0) paymentTerms = 'immediate';
    else if (days === 60) paymentTerms = '60_days';
    else if (days === 90) paymentTerms = '90_days';
    else paymentTerms = '30_days';
  }

  return {
    id: String(raw.id),
    name: raw.name || '',
    cif: raw.cif || '',
    email: raw.email || '',
    phone: raw.phone || '',
    address: raw.address || '',
    paymentMethod: raw.payment_method || 'transfer',
    paymentTerms: paymentTerms,
    discount: Number(raw.discount_pct || 0),
    creditLimit: Number(raw.credit_limit || 0),
    taxRate: Number(raw.tax_rate || 21),
    status: raw.status || 'active',
    contacts: contacts,
    notes: raw.notes || ''
  };
};

const mapBackendContact = (raw: any): ClientContact => ({
  id: String(raw.id),
  name: raw.name || '',
  position: raw.position || '',
  email: raw.email || '',
  phone: raw.phone || '',
  isMain: raw.is_main === true || raw.is_main === 'true' || raw.is_main === 1
});

export const ClientsView = () => {
  const { notify } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteModalClient, setDeleteModalClient] = useState<{ id: string; name: string } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState<Omit<Client, 'id' | 'contacts'>>({
    name: '', cif: '', email: '', phone: '', address: '',
    paymentMethod: 'transfer', paymentTerms: '30_days',
    discount: 10, creditLimit: 30000, taxRate: 21,
    status: 'active', notes: ''
  });
  const [formContacts, setFormContacts] = useState<ClientContact[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Temporary contact state for the form
  const [tempContact, setTempContact] = useState<Omit<ClientContact, 'id'>>({
    name: '', position: '', email: '', phone: '', isMain: false
  });

  const getAccessToken = () => {
    const userCookie = cookies.get('101Obex');
    return userCookie?.token || '';
  };

  // Fetch client contacts helper
  const fetchClientContacts = async (clientId: string): Promise<ClientContact[]> => {
    const token = getAccessToken();
    if (!token) return [];
    try {
      let res = await ObexRequestsHandlerP80.get(`/clients-contacts-list?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(clientId)}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const contactsList = res.response || res.data || res;
      if (Array.isArray(contactsList)) {
        return contactsList.map(mapBackendContact);
      }
    } catch (err) {
      console.error('Error fetching contacts for client:', clientId, err);
    }
    return [];
  };

  // Fetch all clients (list)
  const fetchClients = async () => {
    const token = getAccessToken();
    if (!token) return;
    setLoading(true);
    try {
      let url = `/client-list?access_token=${encodeURIComponent(token)}&page=1&page_size=100`;
      let res = await ObexRequestsHandlerP80.get(url);
      if (res.data) res = res.data;
      if (res.result) res = res.result;

      let listData: any[] = [];
      if (res && res.response && Array.isArray(res.response.data)) {
        listData = res.response.data;
      } else if (res && res.response && Array.isArray(res.response)) {
        listData = res.response;
      } else if (Array.isArray(res.data)) {
        listData = res.data;
      } else if (Array.isArray(res)) {
        listData = res;
      }

      const mappedClients = await Promise.all(
        listData.map(async (item) => {
          const contacts = await fetchClientContacts(String(item.id));
          return mapBackendToClient(item, contacts);
        })
      );

      setClients(mappedClients);
      setSelectedClient(prev => (prev ? mappedClients.find(c => c.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch specific client detail (get)
  const handleSelectClient = async (client: Client) => {
    setIsFormOpen(false);
    setSelectedClient(client);

    const token = getAccessToken();
    if (!token) return;

    try {
      let res = await ObexRequestsHandlerP80.get(`/clients-get?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(client.id)}`);
      if (res.data) res = res.data;
      if (res.result) res = res.result;
      const clientDetail = res.response || res;
      if (clientDetail && typeof clientDetail === 'object' && clientDetail.id) {
        const contacts = await fetchClientContacts(String(clientDetail.id));
        const fullClient = mapBackendToClient(clientDetail, contacts);
        // Cada clic lanza su propia petición: si el usuario ya se movió a otro
        // cliente, esta respuesta llega tarde y no debe robar la selección.
        setSelectedClient(prev => (prev && prev.id === fullClient.id ? fullClient : prev));
        setClients(prev => prev.map(c => c.id === fullClient.id ? fullClient : c));
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
    }
  };

  const handleCreateNewClick = () => {
    setSelectedClient(null);
    setIsEditing(false);
    setFormData({
      name: '', cif: '', email: '', phone: '', address: '',
      paymentMethod: 'transfer', paymentTerms: '30_days',
      discount: 10, creditLimit: 30000, taxRate: 21,
      status: 'active', notes: ''
    });
    setFormContacts([]);
    setTempContact({ name: '', position: '', email: '', phone: '', isMain: false });
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (client: Client) => {
    setSelectedClient(client);
    setIsEditing(true);
    setFormData({
      name: client.name, cif: client.cif, email: client.email, phone: client.phone,
      address: client.address, paymentMethod: client.paymentMethod,
      paymentTerms: client.paymentTerms, discount: client.discount,
      creditLimit: client.creditLimit, taxRate: client.taxRate,
      status: client.status, notes: client.notes || ''
    });
    setFormContacts([...client.contacts]);
    setTempContact({ name: '', position: '', email: '', phone: '', isMain: false });
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleAddContact = async () => {
    if (!tempContact.name.trim()) {
      notify('Aviso', 'El nombre del contacto es obligatorio.', 'warning');
      return;
    }

    if (isEditing && selectedClient) {
      const token = getAccessToken();
      if (token) {
        try {
          const addUrl = `/clients-contacts-add?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(selectedClient.id)}&name=${encodeURIComponent(tempContact.name.trim())}${tempContact.position ? `&position=${encodeURIComponent(tempContact.position.trim())}` : ''}${tempContact.email ? `&email=${encodeURIComponent(tempContact.email.trim())}` : ''}${tempContact.phone ? `&phone=${encodeURIComponent(tempContact.phone.trim())}` : ''}&is_main=${tempContact.isMain ? 'true' : 'false'}`;
          let res = await ObexRequestsHandlerP80.get(addUrl);
          if (res.data) res = res.data;
          if (res.result) res = res.result;
          const newContactRes = res.response || res;
          if (newContactRes && newContactRes.id) {
            const newContact = mapBackendContact(newContactRes);
            let updatedContacts = [...formContacts];
            if (newContact.isMain) {
              updatedContacts = updatedContacts.map(c => ({ ...c, isMain: false }));
            }
            updatedContacts.push(newContact);
            setFormContacts(updatedContacts);
            setTempContact({ name: '', position: '', email: '', phone: '', isMain: false });
            return;
          }
        } catch (err) {
          console.error('Error adding contact to backend:', err);
          notify('Error', 'Error al añadir el contacto.', 'urgent');
        }
      }
    }

    const newContact: ClientContact = {
      id: `CON-${Date.now()}`,
      name: tempContact.name.trim(),
      position: tempContact.position.trim(),
      email: tempContact.email.trim(),
      phone: tempContact.phone.trim(),
      isMain: tempContact.isMain
    };

    let updatedContacts = [...formContacts];
    if (newContact.isMain) {
      updatedContacts = updatedContacts.map(c => ({ ...c, isMain: false }));
    } else if (updatedContacts.length === 0) {
      newContact.isMain = true;
    }

    setFormContacts([...updatedContacts, newContact]);
    setTempContact({ name: '', position: '', email: '', phone: '', isMain: false });
  };

  const handleRemoveContact = async (id: string) => {
    if (isEditing && selectedClient && !id.startsWith('CON-')) {
      const token = getAccessToken();
      if (token) {
        try {
          const remUrl = `/clients-contacts-remove?access_token=${encodeURIComponent(token)}&contact_id=${encodeURIComponent(id)}`;
          await ObexRequestsHandlerP80.get(remUrl);
        } catch (err) {
          console.error('Error removing contact:', err);
          notify('Error', 'Error al eliminar el contacto.', 'urgent');
        }
      }
    }

    const updated = formContacts.filter(c => c.id !== id);
    if (updated.length > 0 && !updated.some(c => c.isMain)) {
      updated[0].isMain = true;
    }
    setFormContacts(updated);
  };

  const handleSaveClient = async () => {
    if (!formData.name.trim() || !formData.cif.trim()) {
      setFormError('El nombre o razón social y el CIF son obligatorios.');
      return;
    }
    const cifRegex = /^[A-HJ-NP-SU-W][0-9]{8}$/i;
    if (!cifRegex.test(formData.cif.trim())) {
      setFormError('El formato de CIF no es válido (Ej: A82345678).');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      setFormError('Sesión no válida o expirada.');
      return;
    }

    let paymentTermsDays = 30;
    if (formData.paymentTerms === 'immediate') paymentTermsDays = 0;
    else if (formData.paymentTerms === '60_days') paymentTermsDays = 60;
    else if (formData.paymentTerms === '90_days') paymentTermsDays = 90;

    try {
      if (isEditing && selectedClient) {
        // UPDATE CLIENT
        let updUrl = `/clients-update?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(selectedClient.id)}&name=${encodeURIComponent(formData.name.trim())}&cif=${encodeURIComponent(formData.cif.toUpperCase().trim())}&payment_method=${encodeURIComponent(formData.paymentMethod)}&payment_terms_days=${paymentTermsDays}&discount_pct=${encodeURIComponent(formData.discount)}&credit_limit=${encodeURIComponent(formData.creditLimit)}&tax_rate=${encodeURIComponent(formData.taxRate)}&status=${encodeURIComponent(formData.status)}`;
        if (formData.email) updUrl += `&email=${encodeURIComponent(formData.email.trim())}`;
        if (formData.phone) updUrl += `&phone=${encodeURIComponent(formData.phone.trim())}`;
        if (formData.address) updUrl += `&address=${encodeURIComponent(formData.address.trim())}`;
        if (formData.notes) updUrl += `&notes=${encodeURIComponent(formData.notes.trim())}`;

        let res = await ObexRequestsHandlerP80.get(updUrl);
        if (res.data) res = res.data;
        if (res.result) res = res.result;

        if (res && (res.response?.id || res.id)) {
          const updatedRaw = res.response || res;
          const updatedClient = mapBackendToClient(updatedRaw, formContacts);
          setClients(prev => prev.map(c => c.id === selectedClient.id ? updatedClient : c));
          setSelectedClient(updatedClient);
          setIsFormOpen(false);
          notify('Éxito', 'Cliente modificado exitosamente', 'info');
        } else {
          const errMessage = typeof res?.response === 'string' ? res.response : 'Error al actualizar el cliente';
          setFormError(errMessage);
        }
      } else {
        // CREATE CLIENT
        let createUrl = `/clients-create?access_token=${encodeURIComponent(token)}&name=${encodeURIComponent(formData.name.trim())}&cif=${encodeURIComponent(formData.cif.toUpperCase().trim())}&payment_method=${encodeURIComponent(formData.paymentMethod)}&payment_terms_days=${paymentTermsDays}&discount_pct=${encodeURIComponent(formData.discount)}&credit_limit=${encodeURIComponent(formData.creditLimit)}&tax_rate=${encodeURIComponent(formData.taxRate)}&status=${encodeURIComponent(formData.status)}`;
        if (formData.email) createUrl += `&email=${encodeURIComponent(formData.email.trim())}`;
        if (formData.phone) createUrl += `&phone=${encodeURIComponent(formData.phone.trim())}`;
        if (formData.address) createUrl += `&address=${encodeURIComponent(formData.address.trim())}`;
        if (formData.notes) createUrl += `&notes=${encodeURIComponent(formData.notes.trim())}`;

        let res = await ObexRequestsHandlerP80.get(createUrl);
        if (res.data) res = res.data;
        if (res.result) res = res.result;

        if (res && (res.response?.id || res.id)) {
          const createdRaw = res.response || res;
          const newClientId = String(createdRaw.id);

          // Add contacts created during form filling
          const savedContacts: ClientContact[] = [];
          for (const contact of formContacts) {
            try {
              const addContactUrl = `/clients-contacts-add?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(newClientId)}&name=${encodeURIComponent(contact.name)}${contact.position ? `&position=${encodeURIComponent(contact.position)}` : ''}${contact.email ? `&email=${encodeURIComponent(contact.email)}` : ''}${contact.phone ? `&phone=${encodeURIComponent(contact.phone)}` : ''}&is_main=${contact.isMain ? 'true' : 'false'}`;
              let cRes = await ObexRequestsHandlerP80.get(addContactUrl);
              if (cRes.data) cRes = cRes.data;
              if (cRes.result) cRes = cRes.result;
              if (cRes?.response?.id || cRes?.id) {
                savedContacts.push(mapBackendContact(cRes.response || cRes));
              } else {
                savedContacts.push(contact);
              }
            } catch (cErr) {
              console.error('Error attaching contact to created client:', cErr);
              savedContacts.push(contact);
            }
          }

          const createdClient = mapBackendToClient(createdRaw, savedContacts.length > 0 ? savedContacts : formContacts);
          setClients(prev => [createdClient, ...prev]);
          setSelectedClient(createdClient);
          setIsFormOpen(false);
          notify('Éxito', 'Cliente dado de alta exitosamente', 'info');
        } else {
          const errMessage = typeof res?.response === 'string' ? res.response : 'Error al dar de alta al cliente';
          setFormError(errMessage);
        }
      }
    } catch (err) {
      console.error('Error saving client:', err);
      setFormError('Error de comunicación con el servidor.');
    }
  };

  const handleDeleteClient = (id: string, name: string) => {
    setDeleteModalClient({ id, name });
  };

  const confirmDeleteClient = async () => {
    if (!deleteModalClient) return;
    const { id } = deleteModalClient;
    const token = getAccessToken();
    if (!token) {
      setDeleteModalClient(null);
      return;
    }

    try {
      const delUrl = `/clients-delete?access_token=${encodeURIComponent(token)}&client_id=${encodeURIComponent(id)}`;
      let res = await ObexRequestsHandlerP80.get(delUrl);
      if (res.data) res = res.data;
      if (res.result) res = res.result;

      const remaining = clients.filter(c => c.id !== id);
      setClients(remaining);
      setSelectedClient(null);
      setIsFormOpen(false);
      notify('Éxito', 'Cliente dado de baja exitosamente', 'info');
    } catch (err) {
      console.error('Error deleting client:', err);
      notify('Error', 'Error al dar de baja el cliente.', 'urgent');
    } finally {
      setDeleteModalClient(null);
    }
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.cif.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedClient(null);
  };

  // Clic fuera cierra la ficha. El formulario no: esta vista no avisa de
  // cambios sin guardar y se perdería lo escrito; se cierra con la X.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedClient) && !isFormOpen);

  const columns: ColumnDef<Client>[] = [
    {
      id: 'id',
      header: 'CLI_ID',
      render: (client) => <span className="clients-view__list-id">{client.id}</span>
    },
    {
      id: 'name',
      header: 'CLIENTE / CIF',
      render: (client) => (
        <>
          <div className="clients-view__list-name">{client.name}</div>
          <div className="clients-view__list-sub">CIF: {client.cif}</div>
        </>
      )
    },
    {
      id: 'discount',
      header: 'DT_COMERCIAL',
      align: 'center',
      render: (client) => <span className="clients-view__list-stat-value">{client.discount}% Dto</span>
    },
    {
      id: 'credit',
      header: 'LÍM_RIESGO',
      align: 'right',
      render: (client) => <span className="clients-view__list-stat-value">{client.creditLimit.toLocaleString()}€</span>
    },
    {
      id: 'contact',
      header: 'CONTACTO_PRINCIPAL',
      render: (client) => {
        const mainContact = client.contacts.find(c => c.isMain);
        return mainContact ? (
          <div className="clients-view__main-contact-line">
            <UserCheck size={10} className="clients-view__main-contact-icon" />
            <span>{mainContact.name} ({mainContact.position})</span>
          </div>
        ) : <span className="clients-view__list-sub">—</span>;
      }
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (client) => <span className={`module-badge ${client.status}`}>{client.status}</span>
    }
  ];

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedClient || isFormOpen)} drawer>
        <div className="clients-view">
          <div className="module-header-row">
            <ModuleHeader
              title="Gestor_De_Clientes"
              subtitle="Administración de cuentas de clientes, personas de contacto, condiciones comerciales y límites de riesgo."
            />
            <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
              <Plus size={14} /> Alta_Cliente
            </button>
          </div>

          <div className="module-toolbar">
            <div className="module-search-wrapper">
              <Search className="module-search-icon" size={14} />
              <input
                type="text"
                placeholder="FILTRAR CLIENTES POR RAZÓN SOCIAL, CIF O ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="module-search-input"
              />
            </div>
          </div>

          <DataTable
            data={filteredClients}
            columns={columns}
            title={<><Users size={14} className="module-panel-title-icon" /> Cartera_Clientes</>}
            subtitle={loading ? undefined : `TOTAL: ${filteredClients.length}`}
            isLoading={loading}
            getRowId={(client) => client.id}
            onRowClick={handleSelectClient}
            selectedRowId={selectedClient?.id}
            emptyText="No se encontraron clientes registrados."
            actionsHeader="ACCIONES"
            actionsColumn={(client) => (
              <RowActionsMenu
                actions={[
                  viewAction(() => handleSelectClient(client)),
                  editAction(() => handleEditClick(client)),
                  deleteAction(() => handleDeleteClient(client.id, client.name), { label: 'Dar de baja' })
                ]}
              />
            )}
          />
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={isEditing ? 'Modificar_Ficha_Cliente' : 'Alta_De_Nuevo_Cliente'}
            onClose={closePanel}
            submitLabel="Guardar_Cliente"
            onSubmit={handleSaveClient}
            showDelete={isEditing && Boolean(selectedClient)}
            onDelete={() => selectedClient && handleDeleteClient(selectedClient.id, selectedClient.name)}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection icon={Building2} title="1. Datos_Identificativos">
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Razón Social o Nombre Completo *</label>
                  <input type="text" required value={formData.name} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, name: val })); }} placeholder="Librerías Díaz de Santos S.A." className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">CIF / NIF *</label>
                  <input type="text" required value={formData.cif} onChange={(e) => { const val = e.target.value.toUpperCase(); setFormData(prev => ({ ...prev, cif: val })); }} placeholder="A82345678" className="sidebar-form-input" />
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Email General</label>
                  <input type="email" value={formData.email} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, email: val })); }} className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono Principal</label>
                  <input type="text" value={formData.phone} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, phone: val })); }} className="sidebar-form-input" />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado</label>
                <select value={formData.status} onChange={(e) => { const val = e.target.value as any; setFormData(prev => ({ ...prev, status: val })); }} className="sidebar-form-input">
                  <option value="active">ACTIVO</option>
                  <option value="inactive">INACTIVO</option>
                  <option value="blocked">BLOQUEADO POR RIESGO</option>
                </select>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Dirección de Envío y Facturación</label>
                <input type="text" value={formData.address} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, address: val })); }} className="sidebar-form-input" />
              </div>
            </DetailSection>

            <DetailSection icon={DollarSign} title="2. Condiciones_Comerciales">
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Método de Pago</label>
                  <select value={formData.paymentMethod} onChange={(e) => { const val = e.target.value as any; setFormData(prev => ({ ...prev, paymentMethod: val })); }} className="sidebar-form-input">
                    <option value="transfer">TRANSFERENCIA BANCARIA</option>
                    <option value="direct_debit">GIRO DIRECTO / DOMICILIADO</option>
                    <option value="credit_card">TARJETA DE CRÉDITO</option>
                    <option value="cash">EFECTIVO</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Vencimiento</label>
                  <select value={formData.paymentTerms} onChange={(e) => { const val = e.target.value as any; setFormData(prev => ({ ...prev, paymentTerms: val })); }} className="sidebar-form-input">
                    <option value="immediate">PAGO INMEDIATO</option>
                    <option value="30_days">30 DÍAS FECHA FACTURA</option>
                    <option value="60_days">60 DÍAS FECHA FACTURA</option>
                    <option value="90_days">90 DÍAS FECHA FACTURA</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Descuento Comercial (%)</label>
                  <input type="number" min="0" max="100" value={formData.discount} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, discount: val })); }} className="sidebar-form-input" />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Límite Crédito (€)</label>
                  <input type="number" min="0" value={formData.creditLimit} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, creditLimit: val })); }} className="sidebar-form-input" />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Tipo Impuesto (IVA %)</label>
                <select value={formData.taxRate} onChange={(e) => { const val = Number(e.target.value); setFormData(prev => ({ ...prev, taxRate: val })); }} className="sidebar-form-input">
                  <option value="4">4% - LIBROS / SUPERREDUCIDO</option>
                  <option value="21">21% - GENERAL</option>
                  <option value="0">0% - EXENTO</option>
                </select>
              </div>
            </DetailSection>

            <DetailSection icon={UserCheck} title="3. Personas_De_Contacto">
              <div className="clients-view__contact-entry">
                <div className="sidebar-form-grid-2">
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Nombre</label>
                    <input type="text" value={tempContact.name} onChange={(e) => { const val = e.target.value; setTempContact(prev => ({ ...prev, name: val })); }} className="sidebar-form-input" />
                  </div>
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Puesto</label>
                    <input type="text" value={tempContact.position} onChange={(e) => { const val = e.target.value; setTempContact(prev => ({ ...prev, position: val })); }} className="sidebar-form-input" />
                  </div>
                </div>
                <div className="sidebar-form-grid-2" style={{ marginTop: '0.75rem' }}>
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Email</label>
                    <input type="email" value={tempContact.email} onChange={(e) => { const val = e.target.value; setTempContact(prev => ({ ...prev, email: val })); }} className="sidebar-form-input" />
                  </div>
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Teléfono</label>
                    <input type="text" value={tempContact.phone} onChange={(e) => { const val = e.target.value; setTempContact(prev => ({ ...prev, phone: val })); }} className="sidebar-form-input" />
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <label className="clients-view__main-checkbox-label">
                    <input type="checkbox" checked={tempContact.isMain} onChange={(e) => setTempContact(prev => ({ ...prev, isMain: e.target.checked }))} />
                    Principal
                  </label>
                  <button type="button" onClick={handleAddContact} className="clients-view__add-contact-btn">
                    + Añadir Contacto
                  </button>
                </div>
              </div>

              {formContacts.length > 0 && (
                <div className="clients-view__form-contacts-grid">
                  {formContacts.map((c) => (
                    <div key={c.id} className="clients-view__form-contact-card">
                      <div>
                        <div className="clients-view__form-contact-name">
                          {c.name}
                          {c.isMain && <span className="clients-view__contact-main-badge">PRINCIPAL</span>}
                        </div>
                        <div className="clients-view__form-contact-pos">{c.position}</div>
                        <div className="clients-view__form-contact-meta">{c.email} • {c.phone}</div>
                      </div>
                      <button type="button" onClick={() => handleRemoveContact(c.id)} className="clients-view__remove-contact-btn"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Observaciones</label>
              <textarea value={formData.notes} onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, notes: val })); }} rows={2} className="sidebar-form-input" />
            </div>
          </SidebarForm>
        ) : selectedClient ? (
          <DetailPanel
            drawer
            title="Ficha_De_Cliente"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedClient)} />
            }
            cards={
              <>
                <DetailCard icon={UserCheck} title={`Contactos_Operativos (${selectedClient.contacts.length})`}>
                  {selectedClient.contacts.length === 0 ? (
                    <div className="clients-view__no-contacts">Sin contactos específicos.</div>
                  ) : (
                    <div>
                      {selectedClient.contacts.map((contact) => (
                        <div key={contact.id} className="clients-view__contact-card">
                          <div className="clients-view__contact-name">
                            {contact.name}
                            {contact.isMain && <span className="clients-view__contact-main-badge">PRINCIPAL</span>}
                          </div>
                          <div className="clients-view__contact-position">{contact.position}</div>
                          <div className="clients-view__contact-details">
                            {contact.email && (
                              <div className="clients-view__contact-row">
                                <Mail size={11} className="clients-view__contact-row-icon" />
                                <span style={{ textTransform: 'lowercase' }}>{contact.email}</span>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="clients-view__contact-row">
                                <Phone size={11} className="clients-view__contact-row-icon" />
                                <span>{contact.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </DetailCard>
              </>
            }
          >
            <DetailIdentity
              icon={Users}
              reference={selectedClient.id}
              name={selectedClient.name}
              subtitle={
                <>
                  <span className={`module-badge ${selectedClient.status}`}>CLIENTE {selectedClient.status}</span>
                  {' '}CIF: <span className="detail-identity-highlight">{selectedClient.cif}</span>
                </>
              }
            />

            {/* KPI Cards */}
            <div className="clients-view__kpi-grid">
              <div className="clients-view__kpi-card">
                <span className="clients-view__kpi-label">Descuento</span>
                <span className="clients-view__kpi-value green">{selectedClient.discount}%</span>
                <span className="clients-view__kpi-footnote">Pactado</span>
              </div>
              <div className="clients-view__kpi-card">
                <span className="clients-view__kpi-label">Riesgo</span>
                <span className="clients-view__kpi-value">{selectedClient.creditLimit.toLocaleString()}€</span>
                <span className="clients-view__kpi-footnote">Límite</span>
              </div>
              <div className="clients-view__kpi-card">
                <span className="clients-view__kpi-label">Vencimiento</span>
                <span className="clients-view__kpi-value" style={{ fontSize: '0.625rem' }}>{PAYMENT_TERMS_LABELS[selectedClient.paymentTerms] || selectedClient.paymentTerms}</span>
                <span className="clients-view__kpi-footnote">General</span>
              </div>
              <div className="clients-view__kpi-card">
                <span className="clients-view__kpi-label">IVA General</span>
                <span className="clients-view__kpi-value">{selectedClient.taxRate}%</span>
                <span className="clients-view__kpi-footnote">{selectedClient.taxRate === 4 ? 'Libros' : selectedClient.taxRate === 0 ? 'Exento' : 'General'}</span>
              </div>
            </div>

            <DetailSection icon={FileText} title="Comercial_y_Facturación">
              <DetailFieldList>
                <DetailField label="Forma de Cobro">{PAYMENT_METHOD_LABELS[selectedClient.paymentMethod] || selectedClient.paymentMethod}</DetailField>
                <DetailField label="Vencimiento Facturas">{PAYMENT_TERMS_LABELS[selectedClient.paymentTerms] || selectedClient.paymentTerms}</DetailField>
                {selectedClient.address && (
                  <DetailField label="Dirección Facturación">{selectedClient.address}</DetailField>
                )}
                {selectedClient.phone && (
                  <DetailField label="Teléfono Principal">{selectedClient.phone}</DetailField>
                )}
                {selectedClient.email && (
                  <DetailField label="Email Principal">
                    <span className="detail-field-value lowercase">{selectedClient.email}</span>
                  </DetailField>
                )}
              </DetailFieldList>
            </DetailSection>

            {selectedClient.notes && (
              <DetailNotes title="Observaciones Internas">{selectedClient.notes}</DetailNotes>
            )}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalClient}
        type="urgent"
        title="Dar de baja cliente"
        message={`¿Está seguro de que desea dar de baja al cliente "${deleteModalClient?.name}"?`}
        confirmText="Dar de baja"
        cancelText="Cancelar"
        onConfirm={confirmDeleteClient}
        onCancel={() => setDeleteModalClient(null)}
      />
    </>
  );
};
